import React from 'react'
import i18next from 'i18next'
import {RouteObject} from 'react-router-dom'

import {EcospendReturn} from '@/pages/components/Ecospend/components'

import {AuthArea} from './AuthArea'
import {UnAuthArea} from './UnAuthArea'
import {ROUTES} from './routes'

import {Maintenance} from '@/pages/Maintenance'
import {OnboardingArea} from '@/router/OnboardingArea'

const Login = React.lazy(() => import(/* webpackChunkName: "Login" */ '@/pages/Login'))
const ResetPassword = React.lazy(
    () => import(/* webpackChunkName: "ResetPassword" */ '@/pages/ResetPassword/ResetPassword')
)
const ResetPasswordFailure = React.lazy(
    () =>
        import(
            /* webpackChunkName: "ResetPasswordFailure" */ '@/pages/ResetPasswordFailure/ResetPasswordFailure'
        )
)
const TwoFA = React.lazy(() => import(/* webpackChunkName: "TwoFA" */ '@/pages/TwoFA/TwoFA'))
const RecoveryCode = React.lazy(
    () => import(/* webpackChunkName: "RecoveryCode" */ '@/pages/RecoveryCode/RecoveryCode')
)
const ChooseCompany = React.lazy(
    () => import(/* webpackChunkName: "ChooseMerchant" */ '@/pages/ChooseMerchant/ChooseMerchant')
)
const ConfirmEmail = React.lazy(
    () => import(/* webpackChunkName: "ConfirmEmail" */ '@/pages/ConfirmEmail')
)
const TwoFAConfirm = React.lazy(
    () => import(/* webpackChunkName: "TwoFAConfirm" */ '@/pages/TwoFAConfirm')
)
const TwoFAEnforced = React.lazy(
    () => import(/* webpackChunkName: "TwoFAEnforced" */ '@/pages/TwoFAEnforced')
)
const ChangeOldPassword = React.lazy(
    () => import(/* webpackChunkName: "SetPassword" */ '@/pages/ChangeOldPassword')
)
const SetPasswordSuccess = React.lazy(
    () => import(/* webpackChunkName: "SetPasswordSuccess" */ '@/pages/SetPasswordSuccess')
)
const SignUpValidateEmail = React.lazy(
    () => import(/* webpackChunkName: "ValidateEmail" */ '~/code/pages/signUp/ValidateEmail')
)
const SignUpValidateOtpCode = React.lazy(
    () => import(/* webpackChunkName: "ValidateOtp" */ '~/code/pages/signUp/ValidateOtpCode')
)
const SignUpCreatePassword = React.lazy(
    () => import(/* webpackChunkName: "CreatePassword" */ '~/code/pages/signUp/CreatePassword')
)
const SignUpValidateRefCode = React.lazy(
    () => import(/* webpackChunkName: "ValidateRefCode" */ '~/code/pages/signUp/ValidateRefCode')
)

const Profile = React.lazy(() => import(/* webpackChunkName: "Profile" */ '@/pages/Profile'))
const PayByLink = React.lazy(() => import(/* webpackChunkName: "PayByLink" */ '@/pages/PayByLink'))
const StartApplication = React.lazy(
    () => import(/* webpackChunkName: "StartApplication" */ '@/pages/onboarding/StartApplication')
)
const CompanyDetails = React.lazy(
    () => import(/* webpackChunkName: "CompanyDetailsPage" */ '@/pages/onboarding/CompanyDetails')
)
const CompanyContacts = React.lazy(
    () => import(/* webpackChunkName: "CompanyContactsPage" */ '@/pages/onboarding/CompanyContacts')
)
const OwnersAndDirectors = React.lazy(
    () =>
        import(/* webpackChunkName: "OwnersAndDirectors" */ '@/pages/onboarding/OwnersAndDirectors')
)
const AddOwner = React.lazy(
    () => import(/* webpackChunkName: "AddOwner" */ '@/pages/onboarding/AddOwner')
)
const AddDirector = React.lazy(
    () => import(/* webpackChunkName: "AddDirector" */ '@/pages/onboarding/AddDirector')
)
const BankDetails = React.lazy(
    () => import(/* webpackChunkName: "BankDetails" */ '@/pages/onboarding/BankDetails')
)
const AddBankAccount = React.lazy(
    () => import(/* webpackChunkName: "AddBankAccount" */ '@/pages/onboarding/AddBankAccount')
)
const OutletsInformation = React.lazy(
    () => import(/* webpackChunkName: "StoresInformation" */ '@/pages/onboarding/StoresInformation')
)
const AddOutlet = React.lazy(
    () => import(/* webpackChunkName: "AddOutlet" */ '@/pages/onboarding/AddOutlet')
)
const ReviewSubmit = React.lazy(
    () => import(/* webpackChunkName: "ReviewSubmit" */ '@/pages/onboarding/ReviewSubmit')
)
const ApplicationProgress = React.lazy(
    () =>
        import(
            /* webpackChunkName: "ApplicationProgress" */ '@/pages/onboarding/ApplicationProgress'
        )
)
const Reports = React.lazy(
    () => import(/* webpackChunkName: "Reports" */ '@/pages/Reports/Reports')
)
const Chargebacks = React.lazy(
    () => import(/* webpackChunkName: "Chargebacks" */ '@/pages/Chargebacks')
)
const OnlinePayments = React.lazy(
    () => import(/* webpackChunkName: "OnlinePayments" */ '@/pages/OnlinePayments')
)
const POSPayments = React.lazy(
    () => import(/* webpackChunkName: "POSPayments" */ '~/code/pages/POSPayments')
)
const POSAmexPayments = React.lazy(
    () => import(/* webpackChunkName: "POSAmexPayments" */ '@/pages/POSAmexPayments')
)
const PaymentMethods = React.lazy(
    () => import(/* webpackChunkName: "PaymentMethods" */ '@/pages/payment-methods/PaymentMethods')
)
const VirtualTerminal = React.lazy(
    () => import(/* webpackChunkName: "VirtualTerminal" */ '@/pages/VirtualTerminal')
)
const TabbedPaymentMethods = React.lazy(
    () =>
        import(
            /* webpackChunkName: "TabbedPaymentMethods" */ '@/pages/payment-methods/TabbedPaymentMethods'
        )
)
const Invoices = React.lazy(
    () => import(/* webpackChunkName: "Invoices" */ '@/pages/Billing/pages/Invoices')
)
const Settlements = React.lazy(
    () =>
        import(/* webpackChunkName: "Settlements" */ '~/code/pages/Settlements/PerTransactionDate')
)

const PerSettlementDate = React.lazy(
    () =>
        import(
            /* webpackChunkName: "PerSettlementDate" */ '~/code/pages/Settlements/PerSettlementDate'
        )
)
const ZashEpos = React.lazy(() => import('@/pages/ZashEpos'))

const ReferAFriend = React.lazy(() => import('@/pages/ReferAFriend'))

const DigitalReceipts = React.lazy(() => import('@/pages/DigitalReceipts'))

const AccountInfo = React.lazy(
    () => import(/* webpackChunkName: "AccountInfo" */ '@/pages/business-account/AccountInfo')
)
const CardsManagement = React.lazy(
    () =>
        import(/* webpackChunkName: "CardsManagement" */ '@/pages/business-account/CardsManagement')
)
const MakeTransfer = React.lazy(
    () => import(/* webpackChunkName: "MakeTransfer" */ '@/pages/business-account/MakeTransfer')
)
const TeamManagement = React.lazy(
    () => import(/* webpackChunkName: "TeamManagement" */ '@/pages/TeamManagement')
)
const Exports = React.lazy(() => import(/* webpackChunkName: "Exports" */ '@/pages/Exports'))
const NoPermissions = React.lazy(
    () => import(/* webpackChunkName: "NoPermissions" */ '@/pages/NoPermissions')
)
const NoPage = React.lazy(() => import(/* webpackChunkName: "NoPage" */ '@/pages/NoPage'))
const Settings = React.lazy(() => import(/* webpackChunkName: "Settings" */ '@/pages/Settings'))
const CompanyProfile = React.lazy(
    () => import(/* webpackChunkName: "CompanyProfile" */ '@/pages/CompanyProfile')
)
const BankAccounts = React.lazy(
    () => import(/* webpackChunkName: "BankAccounts" */ '@/pages/BankAccounts')
)
const BankAccountDetails = React.lazy(
    () => import(/* webpackChunkName: "BankAccountDetails" */ '@/pages/BankAccountDetails')
)
const BankAccountCreate = React.lazy(
    () => import(/* webpackChunkName: "BankAccountDetails" */ '@/pages/BankAccountCreate')
)
const ChangeBankAccountDetails = React.lazy(
    () => import(/* webpackChunkName: "BankAccountDetails" */ '@/pages/ChangeBankAccountDetails')
)
const POSOutlets = React.lazy(
    () => import(/* webpackChunkName: "POSOutlets" */ '@/pages/POSOutlets')
)
const POSOutletDetails = React.lazy(
    () => import(/* webpackChunkName: "POSOutletDetails" */ '@/pages/POSOutletDetails')
)
const OnlineOutlets = React.lazy(
    () => import(/* webpackChunkName: "OnlineOutlets" */ '@/pages/OnlineOutlets')
)
const OnlineOutletDetails = React.lazy(
    () => import(/* webpackChunkName: "OnlineOutletDetails" */ '@/pages/OnlineOutletDetails')
)

const DCC = React.lazy(() => import(/* webpackChunkName: "DCC" */ '@/pages/DCC'))
const OverviewBeta = React.lazy(
    () => import(/* webpackChunkName: "OverviewBeta" */ '@/pages/Overview/OverviewBeta')
)

export const routesMap: () => RouteObject[] = () => [
    {
        element: <AuthArea />,
        children: [
            {
                path: ROUTES.home,
                element: <OverviewBeta title={i18next.t('globals:pages.overview')} />
            },
            {
                path: ROUTES.paymentLinks,
                element: <PayByLink title={i18next.t('globals:pages.paymentLinks')} />
            },
            {
                path: ROUTES.onlinePayments,
                element: <OnlinePayments title={i18next.t('globals:pages.onlinePayments')} />
            },
            {
                path: ROUTES.chargebacks,
                element: <Chargebacks title={i18next.t('globals:pages.chargebacks')} />
            },
            {
                path: ROUTES.posPayments,
                element: <POSPayments title={i18next.t('globals:pages.posPayments')} />
            },
            {
                path: ROUTES.posAmexPayments,
                element: <POSAmexPayments title={i18next.t('globals:pages.posAmexPayments')} />
            },
            {
                path: ROUTES.settlements.perTransactionDate,
                element: <Settlements title={i18next.t('globals:pages.settlements')} />
            },
            {
                path: ROUTES.settlements.perSettlementDate,
                element: <PerSettlementDate title={i18next.t('globals:pages.settlements')} />
            },
            {
                path: ROUTES.dcc,
                element: <DCC title={i18next.t('globals:pages.dcc')} />
            },
            {
                path: ROUTES.zashEpos,
                element: <ZashEpos />
            },
            {
                path: ROUTES.referAFriend,
                element: <ReferAFriend />
            },
            {
                path: ROUTES.digitalReceipts,
                element: <DigitalReceipts />
            },
            {
                path: ROUTES.billing.invoices,
                element: <Invoices title={i18next.t('globals:pages.invoices')} />
            },
            {
                path: ROUTES.virtualTerminal,
                element: <VirtualTerminal title={i18next.t('globals:pages.virtualTerminal')} />
            },
            {
                path: ROUTES.reports,
                element: <Reports title={i18next.t('globals:pages.reports')} />
            },
            {
                path: ROUTES.profile,
                element: <Profile title={i18next.t('globals:pages.profile')} />
            },
            {
                path: ROUTES.exports,
                element: <Exports title={i18next.t('globals:pages.exports')} />
            },
            {
                path: ROUTES.settings.base,
                element: <Settings title={i18next.t('globals:pages.settings')} />
            },
            {
                path: ROUTES.settings.companyProfile,
                element: <CompanyProfile title={i18next.t('globals:pages.companyProfile')} />
            },
            {
                path: ROUTES.settings.bankAccounts,
                element: <BankAccounts title={i18next.t('globals:pages.bankAccounts')} />
            },
            {
                path: `${ROUTES.settings.bankAccounts}/:id`,
                element: <BankAccountDetails title={i18next.t('globals:pages.bankAccounts')} />
            },
            {
                path: ROUTES.settings.bankAccountCreate,
                element: <BankAccountCreate title={i18next.t('globals:pages.bankAccountCreate')} />
            },
            {
                path: `${ROUTES.settings.posOutlets}/:id/${ROUTES.settings.changeBankAccountDetails}/:type`,
                element: (
                    <ChangeBankAccountDetails
                        title={i18next.t('globals:pages.changeBankAccountDetails')}
                    />
                )
            },
            {
                path: `${ROUTES.settings.onlineOutlets}/:id/${ROUTES.settings.changeBankAccountDetails}/:type`,
                element: (
                    <ChangeBankAccountDetails
                        title={i18next.t('globals:pages.changeBankAccountDetails')}
                    />
                )
            },
            {
                path: ROUTES.settings.posOutlets,
                element: <POSOutlets title={i18next.t('globals:pages.posOutlets')} />
            },
            {
                path: `${ROUTES.settings.posOutlets}/:id`,
                element: <POSOutletDetails title={i18next.t('globals:pages.posOutlets')} />
            },
            {
                path: ROUTES.settings.onlineOutlets,
                element: <OnlineOutlets title={i18next.t('globals:pages.onlineOutlets')} />
            },
            {
                path: `${ROUTES.settings.onlineOutlets}/:id`,
                element: <OnlineOutletDetails title={i18next.t('globals:pages.onlineOutlets')} />
            },
            {
                path: ROUTES.settings.teamManagement,
                element: <TeamManagement title={i18next.t('globals:pages.teamManagement')} />
            },
            {
                path: ROUTES.settings.paymentMethods,
                element: <PaymentMethods title={i18next.t('globals:pages.paymentMethods')} />
            },
            {
                path: ROUTES.settings.paymentMethodDetails,
                element: <TabbedPaymentMethods title={i18next.t('globals:pages.paymentMethods')} />
            },
            {
                path: ROUTES.settings.teamManagement,
                element: <TeamManagement title={i18next.t('globals:pages.teamManagement')} />
            },
            {
                path: ROUTES.noPermissions,
                element: <NoPermissions title={i18next.t('globals:pages.noPermissions')} />
            },
            {
                path: ROUTES.businessAccount.accountInfo,
                element: <AccountInfo title={i18next.t('globals:pages.accountInfo')} />
            },
            {
                path: ROUTES.businessAccount.cardsManagement,
                element: <CardsManagement title={i18next.t('globals:pages.cardsManagement')} />
            },
            {
                path: ROUTES.businessAccount.makeTransfer,
                element: <MakeTransfer title={i18next.t('globals:pages.makeTransfer')} />
            },
            {
                path: ROUTES.ecospendReturn,
                element: <EcospendReturn title={i18next.t('globals:pages.ecospendReturn')} />
            }
        ]
    },
    {
        element: <OnboardingArea />,
        children: [
            {
                path: ROUTES.onboarding.base,
                element: <StartApplication title={i18next.t('globals:pages.activation')} />
            },
            {
                path: ROUTES.onboarding.companyDetails,
                element: <CompanyDetails title={i18next.t('globals:pages.companyDetails')} />
            },
            {
                path: ROUTES.onboarding.companyContacts,
                element: <CompanyContacts title={i18next.t('globals:pages.companyContacts')} />
            },
            {
                path: ROUTES.onboarding.ownersDirectors,
                element: (
                    <OwnersAndDirectors title={i18next.t('globals:pages.OwnersAndDirectors')} />
                )
            },
            {
                path: ROUTES.onboarding.ownerCreate,
                element: <AddOwner title={i18next.t('globals:pages.ownerCreate')} />
            },
            {
                path: ROUTES.onboarding.directorCreate,
                element: <AddDirector title={i18next.t('globals:pages.directorCreate')} />
            },
            {
                path: ROUTES.onboarding.bankDetails,
                element: <BankDetails title={i18next.t('globals:pages.bankDetails')} />
            },
            {
                path: ROUTES.onboarding.bankAccountCreate,
                element: <AddBankAccount title={'Add Bank Account'} />
            },
            {
                path: ROUTES.onboarding.outletsInformation,
                element: (
                    <OutletsInformation title={i18next.t('globals:pages.outletsInformation')} />
                )
            },
            {
                path: ROUTES.onboarding.outletCreate,
                element: <AddOutlet title={'Add Outlet'} />
            },
            {
                path: ROUTES.onboarding.reviewSubmit,
                element: <ReviewSubmit title={i18next.t('globals:pages.reviewSubmit')} />
            },
            {
                path: ROUTES.onboarding.applicationProgress,
                element: (
                    <ApplicationProgress title={i18next.t('globals:pages.applicationProgress')} />
                )
            }
        ]
    },
    {
        element: <UnAuthArea />,
        children: [
            {
                path: ROUTES.login,
                element: <Login title={i18next.t('globals:pages.login')} />
            },
            {
                path: ROUTES.resetPassword,
                element: <ResetPassword title={i18next.t('globals:pages.resetPassword')} />
            },
            {
                path: ROUTES.resetPasswordFailure,
                element: <ResetPasswordFailure title={i18next.t('globals:pages.resetPassword')} />
            },
            {
                path: ROUTES.twoFA,
                element: <TwoFA title={i18next.t('globals:pages.twoFA')} />
            },
            {
                path: ROUTES.recoveryCode,
                element: <RecoveryCode title={i18next.t('globals:pages.recoveryCode')} />
            },
            {
                path: ROUTES.chooseCompany,
                element: <ChooseCompany title={i18next.t('globals:pages.chooseCompany')} />
            },
            {
                path: ROUTES.confirmEmail,
                element: <ConfirmEmail title={i18next.t('globals:pages.confirmEmail')} />
            },
            {
                path: ROUTES.twoFAEnforced,
                element: <TwoFAEnforced title={i18next.t('globals:pages.twoFAEnforced')} />
            },
            {
                path: ROUTES.confirmTwoFA,
                element: <TwoFAConfirm title={i18next.t('globals:pages.confirmTwoFA')} />
            },
            {
                path: ROUTES.setPassword,
                element: <ChangeOldPassword title={i18next.t('globals:pages.setPassword')} />
            },
            {
                path: ROUTES.setPasswordSuccess,
                element: <SetPasswordSuccess title={i18next.t('globals:pages.setPassword')} />
            },
            {
                path: ROUTES.signUpValidateEmail,
                element: (
                    <SignUpValidateEmail title={i18next.t('globals:pages.signup.validateEmail')} />
                )
            },
            {
                path: ROUTES.signUpValidateOtpCode,
                element: (
                    <SignUpValidateOtpCode title={i18next.t('globals:pages.signup.validateOtp')} />
                )
            },
            {
                path: ROUTES.signUpCreatePassword,
                element: (
                    <SignUpCreatePassword
                        title={i18next.t('globals:pages.signup.createPassword')}
                    />
                )
            },
            {
                path: ROUTES.signUpValidateRefCode,
                element: (
                    <SignUpValidateRefCode
                        title={i18next.t('globals:pages.signup.validateRefCode')}
                    />
                )
            },
            {
                path: ROUTES.noPage,
                element: <NoPage title={i18next.t('globals:pages.noPage')} />
            }
        ]
    },
    {
        path: ROUTES.maintenance,
        element: <Maintenance />
    }
]
